'use client';

import Dialog from '@/components/Dialog';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { useCallback, useEffect } from 'react';

export default function AwsCaptchaDialog() {
  // 最早的调试点：在任何 hooks 之前
  logger.log('[AwsCaptchaDialog] Component render started');

  const awsCaptchaDialog = useStore((state) => state.awsCaptchaDialog);
  const setAwsCaptchaDialogShow = useStore((state) => state.setAwsCaptchaDialogShow);
  const awsWafApiKey = env.NEXT_PUBLIC_AWS_WAF_API_KEY;

  logger.log('[AwsCaptchaDialog] After hooks, awsCaptchaDialog:', awsCaptchaDialog);

  // 添加调试日志
  useEffect(() => {
    logger.log('[AwsCaptchaDialog] Component mounted/updated');
    logger.log('[AwsCaptchaDialog] awsCaptchaDialog state:', awsCaptchaDialog);
    logger.log('[AwsCaptchaDialog] awsWafApiKey:', awsWafApiKey);
    logger.log('[AwsCaptchaDialog] window.AwsWafCaptcha:', window.AwsWafCaptcha);
  }, [awsCaptchaDialog, awsWafApiKey]);

  const onCaptchaEvent = useCallback(
    (event: string) => {
      logger.log('onCaptchaEvent', event);
      // 只在特定事件时关闭对话框
      if (event === 'onSuccess' || event === 'onError') {
        setAwsCaptchaDialogShow(false);
      }
    },
    [setAwsCaptchaDialogShow],
  );

  const renderCaptcha = useCallback(() => {
    // 检查 AWS WAF Captcha SDK 是否已加载
    if (!window.AwsWafCaptcha) {
      logger.error('AWS WAF Captcha SDK not loaded');
      setAwsCaptchaDialogShow(false);
      return Promise.reject(new Error('AWS WAF Captcha SDK not loaded'));
    }

    return new Promise((resolve, reject) => {
      onCaptchaEvent('onCaptchaRequired');

      const container = document.getElementById('aws-captcha-form');
      if (!container) {
        logger.error('AWS Captcha container not found');
        setAwsCaptchaDialogShow(false);
        reject(new Error('Container not found'));
        return;
      }

      try {
        window.AwsWafCaptcha.renderCaptcha(container, {
          dynamicWidth: true,
          onSuccess: (wafToken: string) => {
            logger.log('AWS Captcha success, token:', wafToken);
            onCaptchaEvent('onSuccess');
            resolve(wafToken);
          },
          onLoad: () => {
            document.body.style.cursor = 'default';
            onCaptchaEvent('onLoad');
          },
          onError: (error: any) => {
            logger.error('AWS Captcha error:', error);
            onCaptchaEvent('onError');
            reject(error);
          },
          onPuzzleTimeout: () => onCaptchaEvent('onPuzzleTimeout'),
          onPuzzleIncorrect: () => onCaptchaEvent('onPuzzleIncorrect'),
          onPuzzleCorrect: () => onCaptchaEvent('onPuzzleCorrect'),
          apiKey: awsWafApiKey,
        });
      } catch (error) {
        logger.error('AWS Captcha render failed:', error);
        setAwsCaptchaDialogShow(false);
        reject(error);
      }
    });
  }, [awsWafApiKey, onCaptchaEvent, setAwsCaptchaDialogShow]);

  const handleClose = useCallback(() => {
    setAwsCaptchaDialogShow(false);
  }, [setAwsCaptchaDialogShow]);

  useEffect(() => {
    if (awsCaptchaDialog.show) {
      // AWS WAF Captcha 相关逻辑可以在这里处理
      logger.log('AWS Captcha Dialog opened');

      // 延迟渲染确保 DOM 元素已准备好
      const timer = setTimeout(() => {
        renderCaptcha().catch((error) => {
          logger.error('Failed to render AWS Captcha:', error);
        });
      }, 500);

      // 清理定时器
      return () => clearTimeout(timer);
    }
  }, [awsCaptchaDialog.show, renderCaptcha]);

  return (
    <Dialog
      open={awsCaptchaDialog.show}
      fullWidth={false}
      showCloseIcon
      footer={null}
      title="Captcha Verification"
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          width: { xs: '90vw', md: '400px' },
          margin: '0 auto',
          borderRadius: '12px',
        },
        '& .MuiDialogTitle-root': {
          paddingBottom: 0,
        },
      }}
    >
      <div id="aws-captcha-form" />
    </Dialog>
  );
}
